// 测距离
import 'leaflet.polylinemeasure'
import L from 'leaflet'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'
import type { LatLng, LatLngExpression } from 'leaflet'

interface PolylineMeasureOptions extends L.Control.PolylineMeasureOptions2 {
  scaleTextClass?: string
  scaleOneClass?: string
  scaleTwoClass?: string
}

interface PolylinePath {
  arrowMarkers?: L.Marker[]
  polylinePath: L.Polyline
  circleMarkers: L.CircleMarker[]
  circleCoords: LatLngExpression[]
  tooltips: L.Tooltip[]
}

export class PolylineMeasureExtend extends L.Control.PolylineMeasure {
  [x: string]: any
  constructor(options?: PolylineMeasureOptions) {
    super(options)
  }

  private _clearMeasurementsByID(id: number | string) {
    if (this._layerPaint && this._arrPolylines) {
      const arrPolylinesId = this._arrPolylines[id]
      if (arrPolylinesId) {
        const removeList = ['arrowMarkers', 'circleMarkers', 'tooltips']
        for (let i = 0; i < removeList.length; i++)
          if (arrPolylinesId[removeList[i]]) {
            for (let j = 0; j < arrPolylinesId[removeList[i]].length; j++)
              this._layerPaint.removeLayer(arrPolylinesId[removeList[i]][j])
          }
        if (arrPolylinesId.polylinePath) {
          if (arrPolylinesId.polylinePath.arrowMarkers) {
            for (
              let i = 0;
              i < arrPolylinesId.polylinePath.arrowMarkers.length;
              i++
            )
              this._layerPaint.removeLayer(
                arrPolylinesId.polylinePath.arrowMarkers[i]
              )
          }
          this._layerPaint.removeLayer(arrPolylinesId.polylinePath)
        }
        delete this._arrPolylines[id]
      }
    }
  }

  public addCloseButtonToPolyline(event: any, map: MyMap) {
    const polylinePat = event.polylinePath
    // 添加关闭按钮到线段的最后一个点附近
    const lastMarker = polylinePat.getLatLngs().slice(-1)[0]

    L.marker(lastMarker, {
      icon: L.icon({
        iconUrl: ShipxyOptions.data_img.close_red,
        iconSize: [16, 16],
        iconAnchor: [16, 22],
      }),
    })
      .on('click', (e) => {
        setTimeout(() => {
          e.target.remove()
          this._clearMeasurementsByID(event.id)
          this.options.oneCloseCallBack &&
            this.options.oneCloseCallBack(this._arrPolylines)
        }, 50)
      })
      .addTo(map)
  }

  drawScaleByLine(polylineMeasure: any): L.Marker[] | null {
    const distances: L.Marker[] = []
    if (
      !polylineMeasure ||
      !polylineMeasure.circleMarkers ||
      polylineMeasure.circleMarkers.length < 1
    ) {
      return null
    }
    const totalDistance = this._getDistance(polylineMeasure.distance)
    const stepSize = this.getStepByZoom() / 2

    if (!(totalDistance.value / stepSize < 1)) {
      let accumulatedDistance = 0
      let currentDistance = 0
      let markerCount = 0
      for (let i = 1; i < polylineMeasure.circleMarkers.length; i++) {
        const startMarker = polylineMeasure.circleMarkers[
          i - 1
        ].getLatLng() as LatLng
        const endMarker = polylineMeasure.circleMarkers[i].getLatLng()
        const startCoord = polylineMeasure.circleCoords[i - 1]
        const endCoord = polylineMeasure.circleCoords[i]
        let midPoint = L.latLng(startMarker.lat, startMarker.lng)
        const angle = CanvasShipUtils.getAngleForLatLngByDistFromLatLngs(
          startMarker,
          endMarker
        )
        const distance = this._getDistance(startCoord.distanceTo(endCoord))

        accumulatedDistance += Number(distance)
        const integerDistance = Math.floor(accumulatedDistance)

        for (
          let markerIndex = Math.ceil(currentDistance / stepSize);
          markerIndex < integerDistance;
          markerIndex++
        ) {
          const offset = currentDistance - stepSize

          if (offset > 0) {
            const unitDistance = CanvasShipUtils.unitToM(
              offset,
              totalDistance.unit
            )
            const markerLatLng = CanvasShipUtils.getLatLngByDistFromLatLngs(
              startMarker,
              endMarker,
              angle,
              unitDistance
            )

            if (markerLatLng && this._map.getBounds().contains(markerLatLng)) {
              const angleOffset =
                -Math.atan2(
                  midPoint.lat - markerLatLng.lat,
                  midPoint.lng - markerLatLng.lng
                ) * 57.29578

              let iconHTML = ''
              const scaleClass =
                accumulatedDistance % 2 === 0
                  ? this.options.scaleTextClass
                  : this.options.scaleOneClass
              const scaleContent =
                accumulatedDistance % 2 === 0
                  ? `${currentDistance}&nbsp;${this._unitToCN(
                      totalDistance.unit
                    )}`
                  : '&#921;'

              if (angleOffset >= 128 && angleOffset < 160) {
                iconHTML = `
                  <div class="polyline-measure-scale ${scaleClass}" style="transform: rotate(${angleOffset}deg)">
                    ${scaleContent}
                  </div>
                `
              } else {
                iconHTML = `
                  <div class="polyline-measure-scale ${this.options.scaleTwoClass}" style="transform: rotate(${angleOffset}deg)">
                    ${scaleContent}
                  </div>
                `
              }

              const markerIcon = L.divIcon({
                className: '',
                iconSize: [1, 1],
                iconAnchor: [angleOffset, 0],
                html: iconHTML,
              })

              const newMarker = L.marker(markerLatLng, {
                icon: markerIcon,
                zIndexOffset: -100,
              }).addTo(this._layerPaint)

              distances.push(newMarker)
              midPoint = L.latLng(markerLatLng.lat, markerLatLng.lng)
            }

            currentDistance = (markerCount += 1) * stepSize
            markerIndex = Math.ceil(currentDistance / stepSize)
          }
        }

        currentDistance += Number(distance)
      }

      return distances
    }
    return distances
  }

  _drawScaleAll(polylineData: PolylinePath): void {
    if (polylineData && polylineData.polylinePath) {
      const polyline = polylineData.polylinePath as any
      this.clearScaleAll(polyline)
      const arrows = this.drawScaleByLine(polylineData)
      if (!polyline.arrowMarkers) {
        polyline.arrowMarkers = []
      }
      if (arrows && arrows.length > 0) {
        polyline.arrowMarkers = [...polyline.arrowMarkers, ...arrows]
      }
    }
  }

  clearScaleAll(polylineData: PolylinePath): void {
    if (polylineData && polylineData.arrowMarkers) {
      for (let i = 0, len = polylineData.arrowMarkers.length; i < len; i++) {
        polylineData.arrowMarkers[i].removeFrom(this._layerPaint)
      }
      polylineData.arrowMarkers = []
    }
  }

  getStepByZoom(): number {
    const STEP_BY_ZOOM: Record<number, number> = {
      1: 2000,
      2: 1500,
      3: 1000,
      4: 500,
      5: 200,
      6: 150,
      7: 100,
      8: 50,
      9: 20,
      10: 15,
      11: 10,
      12: 5,
      13: 2,
      14: 1,
      15: 0.5,
      16: 0.2,
      17: 0.15,
      18: 0.1,
    }
    return STEP_BY_ZOOM[this._map.getZoom()] || 0.01
  }

  reDrawAll(): void {
    this.options = this._resetOptionByMapType()
    if (this._unitControl) {
      this._unitControl.title = `${
        this.options.changeUnitsText
      } . ${this._unitToCN(this.options.unit || '')}`
    }
    for (let i = 0; i < this._arrPolylines.length; i++) {
      const polyline = this._arrPolylines[i]
      let cumulativeDistance = 0
      const newCoordinates: LatLngExpression[] = []

      this.clearScaleAll(polyline.polylinePath)

      polyline.circleMarkers.forEach((circleMarker: any, index: number) => {
        if (index >= 1) {
          const segmentDistance = polyline.circleCoords[index - 1].distanceTo(
            polyline.circleCoords[index]
          )
          cumulativeDistance += segmentDistance
          this._updateTooltip(
            polyline.tooltips[index],
            polyline.tooltips[index - 1].getLatLng(),
            cumulativeDistance,
            segmentDistance,
            polyline.circleMarkers[index - 1].getLatLng(),
            polyline.circleMarkers[index].getLatLng()
          )
          const arcPoints = this._polylineArc(
            polyline.circleMarkers[index - 1].getLatLng(),
            polyline.circleMarkers[index].getLatLng()
          )
          newCoordinates.push(...(arcPoints as any))
        }
      })

      this._layerPaint.removeLayer(polyline.polylinePath)
      polyline.polylinePath = L.polyline(newCoordinates, {
        color: this.options.fixedLine.color,
        weight: this.options.fixedLine.weight,
        interactive: false,
      })
        .addTo(this._layerPaint)
        .bringToBack()

      this.drawScaleAll(polyline)

      let currentCircleIndex = 0
      const lastCircleIndex = polyline.circleMarkers.length - 1
      polyline.circleMarkers.forEach(
        (circleMarker: { setStyle: (arg0: any) => void }) => {
          if (currentCircleIndex === 0) {
            circleMarker.setStyle(this.options.startCircle)
          } else if (currentCircleIndex === lastCircleIndex) {
            circleMarker.setStyle(this.options.endCircle)
          } else {
            circleMarker.setStyle(this.options.intermedCircle)
          }
          currentCircleIndex++
        }
      )

      currentCircleIndex = 0
      polyline.tooltips.forEach((tooltip: { _icon: any }) => {
        if (
          currentCircleIndex === 0 ||
          currentCircleIndex === lastCircleIndex
        ) {
          // No action
        } else {
          const currentLatLng =
            polyline.circleMarkers[currentCircleIndex].getLatLng()
          const previousLatLng =
            polyline.circleMarkers[currentCircleIndex - 1].getLatLng()
          const pointCurrent = this._map.latLngToLayerPoint(currentLatLng)
          const pointPrevious = this._map.latLngToLayerPoint(previousLatLng)
          const dx = Math.abs(pointCurrent.x - pointPrevious.x)
          const dy = Math.abs(pointCurrent.y - pointPrevious.y)
          if (dx <= 130 && dy <= 15) {
            console.log(tooltip._icon)
            // $(tooltip._icon).hide()
          } else {
            console.log(tooltip._icon)
            // $(tooltip._icon).show()
          }
        }
        currentCircleIndex++
      })
    }
  }

  _resetOptionByMapType(): PolylineMeasureOptions {
    if ('MT_SATELLITE' === (this._map as any).mapType) {
      this.options.scaleTextClass = 'polyline-measure-scale-txt-wx'
      this.options.scaleOneClass = 'polyline-measure-scale-one-wx'
      this.options.scaleTwoClass = 'polyline-measure-scale-two-wx'

      L.extend(this.options, {
        tempLine: {
          color: '#FFFFFF',
          weight: 0x2,
        },
        fixedLine: {
          color: '#FFFFFF',
          weight: 0x3,
          stroke: true,
        },
        startCircle: {
          weight: 0x2,
          color: '#FFFFFF',
          fillColor: '#88FF88',
          fillOpacity: 0x1,
          radius: 0x4,
        },
        intermedCircle: {
          weight: 0x2,
          color: '#2F302F',
          fillColor: '#2F302F',
          fillOpacity: 0x1,
          radius: 0x4,
        },
        currentCircle: {
          weight: 0x2,
          color: '#2F302F',
          fillColor: '#2F302F',
          fillOpacity: 0x1,
          radius: 0x3,
        },
        endCircle: {
          weight: 0x2,
          color: '#2F302F',
          fillColor: '#e33647',
          fillOpacity: 0x1,
          radius: 0x4,
        },
      })
    } else {
      this.options.scaleTextClass = 'polyline-measure-scale-txt'
      this.options.scaleOneClass = 'polyline-measure-scale-one'
      this.options.scaleTwoClass = 'polyline-measure-scale-two'

      L.extend(this.options, {
        tempLine: {
          color: '#2F302F',
          weight: 0x2,
        },
        fixedLine: {
          color: '#2F302F',
          weight: 0x3,
          stroke: true,
        },
        startCircle: {
          weight: 0x2,
          color: '#2F302F',
          fillColor: '#88FF88',
          fillOpacity: 0x1,
          radius: 0x4,
        },
        intermedCircle: {
          weight: 0x2,
          color: '#2F302F',
          fillColor: '#FFFFFF',
          fillOpacity: 0x1,
          radius: 0x4,
        },
        currentCircle: {
          weight: 0x2,
          color: '#2F302F',
          fillColor: '#FFFFFF',
          fillOpacity: 0x1,
          radius: 0x3,
        },
        endCircle: {
          weight: 0x2,
          color: '#2F302F',
          fillColor: '#e33647',
          fillOpacity: 0x1,
          radius: 0x4,
        },
      })
    }

    return this.options
  }

  _unitToCN(unit: string): string {
    let unitName = '海里'
    switch (unit) {
      case 'mi':
      case 'landmiles':
        unitName = '英里'
        break
      case 'nm':
      case 'nauticalmiles':
        unitName = '海里'
        break
      case 'km':
      case 'metres':
        unitName = '千米'
    }
    return unitName
  }

  _autoMoveMap(marker: any, latlng: LatLng): void {
    marker._map_move = false
    marker.to_lat = 0
    marker.to_lng = 0
    if (marker._map_move_time) {
      clearTimeout(marker._map_move_time)
    }

    const bounds = this._map.getBounds()
    const northEast = bounds.getNorthEast()
    const southWest = bounds.getSouthWest()
    const northEastPoint = this._map.latLngToLayerPoint(northEast)
    const southWestPoint = this._map.latLngToLayerPoint(southWest)
    const markerPoint = this._map.latLngToLayerPoint(latlng)

    if (Math.abs(southWestPoint.y - markerPoint.y) <= 0x32) {
      marker.to_lat = southWest.lat - latlng.lat
    } else if (Math.abs(northEastPoint.y - markerPoint.y) <= 0x32) {
      marker.to_lat = northEast.lat - latlng.lat
    }

    if (Math.abs(southWestPoint.x - markerPoint.x) <= 0x32) {
      marker.to_lng = southWest.lng - latlng.lng
    } else if (Math.abs(northEastPoint.x - markerPoint.x) <= 0x32) {
      marker.to_lng = northEast.lng - latlng.lng
    }

    this.moveMap(marker)

    if (marker._map_move === 0x1 && this._currentLine !== null) {
      console.log('=======_map_move===========', marker.to_lat, marker.to_lng)
      const newLatLng = L.latLng(
        latlng.lat + marker.to_lat,
        latlng.lng + marker.to_lng
      )
      marker._map_move_time = setTimeout(() => {
        this._autoMoveMap(marker, newLatLng)
      }, 200)
    }
  }

  moveMap(marker: any): void {
    if (
      marker.to_lat !== 0 ||
      (marker.to_lng !== 0 && this._currentLine !== null)
    ) {
      const center = this._map.getCenter()
      const newCenter = L.latLng(
        center.lat + marker.to_lat,
        center.lng + marker.to_lng
      )
      this._map.panTo(newCenter)
      marker._map_move_time = setTimeout(() => {
        this.moveMap(marker)
      }, 200)
    }
  }
}
